/**
 * /ai/runs/:runId — run detail, rendered per kind:
 *   eval            — scorecard + per-doc field diffs
 *   fleet           — triage: status breakdown, clustered check failures,
 *                     novel enum values, needs-review list
 *   canary          — per-doc reproduction results, alarms LOUD
 *   tune            — baseline vs candidate, adopted/rejected, rationale
 *   classify-eval   — type/lender accuracy + confusion + misclassified
 *   export-finetune — gates + storage paths
 *   process/bootstrap — raw report
 * Falls back to pretty-printed JSON for anything unrecognized.
 */
import React from 'react';
import { useParams, Link, useRouteMatch } from 'react-router-dom';
import {
  Box,
  Heading,
  HStack,
  Text,
  Button,
  Spinner,
  Center,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Tag,
  Code,
  Alert,
  AlertIcon,
  SimpleGrid,
  Stat,
  StatLabel,
  StatNumber,
  useColorModeValue,
} from '@chakra-ui/react';
import { FiArrowLeft, FiRefreshCw } from 'react-icons/fi';
import { useRunPoller } from '../../hooks/useDocAI';
import StatusChip from '../../components/AI/StatusChip';
import RunProgressBar from '../../components/AI/RunProgressBar';
import DiffTable from '../../components/AI/DiffTable';
import ValidationChecklist from '../../components/AI/ValidationChecklist';

const fmtPct = (x) => (x == null ? '—' : `${(x * 100).toFixed(2)}%`);

export default function AIRunDetailPage() {
  const { runId } = useParams();
  const { run, isPolling, refresh } = useRunPoller(runId);
  const { url } = useRouteMatch();
  const sectionUrl = url.replace(/\/runs\/.*$/, '');

  const cardBg = useColorModeValue('white', 'gray.800');
  const cardBorder = useColorModeValue('gray.200', 'gray.700');
  const codeBg = useColorModeValue('gray.50', 'gray.900');

  if (!run) {
    return <Center h="60vh"><Spinner size="xl" color="blue.400" /></Center>;
  }

  const report = run.report || {};
  const totals = run.totals || {};

  const statCard = (label, value) => (
    <Stat key={label} bg={cardBg} borderWidth="1px" borderColor={cardBorder} borderRadius="lg" px={4} py={2}>
      <StatLabel fontSize="xs">{label}</StatLabel>
      <StatNumber fontSize="xl">{value}</StatNumber>
    </Stat>
  );

  const renderEval = () => (
    <>
      <SimpleGrid columns={{ base: 2, md: 4 }} spacing={3} mb={5}>
        {statCard('Docs perfect', `${totals.perfectDocs ?? '—'}/${totals.docs ?? '—'}`)}
        {statCard('Field accuracy', fmtPct(totals.fieldAccuracy))}
        {statCard('Fields', `${totals.fieldsCorrect ?? '—'}/${totals.fieldsTotal ?? '—'}`)}
        {statCard('Est. cost', totals.estCostUsd != null ? `$${totals.estCostUsd}` : '—')}
      </SimpleGrid>
      {(report.goldenProblems || []).length > 0 && (
        <Alert status="warning" mb={4} borderRadius="md" fontSize="sm">
          <AlertIcon />
          {report.goldenProblems.length} golden matching problem(s): {report.goldenProblems.map((p) => p.golden).join(', ')}
        </Alert>
      )}
      {(report.docs || []).map((d) => (
        <Box key={d.golden || d.pdf} bg={cardBg} borderWidth="1px" borderColor={cardBorder} borderRadius="lg" p={4} mb={3}>
          <HStack justify="space-between" mb={2} flexWrap="wrap">
            <Text fontSize="sm" fontFamily="mono" noOfLines={1}>{d.golden || d.pdf}</Text>
            <Tag size="sm" colorScheme={d.perfect ? 'green' : 'red'}>
              {d.perfect ? 'PERFECT' : `${(d.diffs || []).length} diffs`}
            </Tag>
          </HStack>
          {!d.perfect && (
            <DiffTable
              rows={(d.diffs || []).map((x) => ({ path: x.path, model: x.actual, corrected: x.expected }))}
              leftLabel="Model"
              rightLabel="Golden"
            />
          )}
        </Box>
      ))}
    </>
  );

  const renderFleet = () => (
    <>
      <SimpleGrid columns={{ base: 2, md: 4 }} spacing={3} mb={5}>
        {statCard('Docs', totals.docs ?? '—')}
        {statCard('Auto-accept', totals.byStatus?.auto_accept ?? 0)}
        {statCard('Review', (totals.byStatus?.review ?? 0) + (totals.byStatus?.error ?? 0))}
        {statCard('Fresh model calls', totals.freshModelCalls ?? 0)}
      </SimpleGrid>
      <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={5} mb={5}>
        <Box bg={cardBg} borderWidth="1px" borderColor={cardBorder} borderRadius="lg" p={4}>
          <Heading size="xs" mb={3}>Status breakdown</Heading>
          {Object.entries(totals.byStatus || {}).map(([s, n]) => (
            <HStack key={s} justify="space-between" py={1}>
              <StatusChip status={s} />
              <Text fontSize="sm">{n}</Text>
            </HStack>
          ))}
        </Box>
        <Box bg={cardBg} borderWidth="1px" borderColor={cardBorder} borderRadius="lg" p={4}>
          <Heading size="xs" mb={3}>Clustered check failures</Heading>
          {Object.entries(report.checkFailureCounts || {})
            .sort((a, b) => b[1] - a[1])
            .slice(0, 12)
            .map(([check, n]) => (
              <HStack key={check} justify="space-between" py={0.5}>
                <Text fontSize="xs" fontFamily="mono" noOfLines={1}>{check}</Text>
                <Text fontSize="xs" fontWeight="bold">{n}</Text>
              </HStack>
            ))}
          {!Object.keys(report.checkFailureCounts || {}).length && (
            <Text fontSize="xs" color="gray.500">No failing checks.</Text>
          )}
        </Box>
      </SimpleGrid>
      {Object.keys(report.novelEnumValues || {}).length > 0 && (
        <Box bg={cardBg} borderWidth="1px" borderColor={cardBorder} borderRadius="lg" p={4} mb={5}>
          <Heading size="xs" mb={3}>Novel enum values (candidates for enums.json)</Heading>
          {Object.entries(report.novelEnumValues).map(([path, values]) => (
            <Box key={path} mb={2}>
              <Text fontSize="xs" fontFamily="mono" color="gray.500">{path}</Text>
              <HStack spacing={2} flexWrap="wrap">
                {Object.entries(values).map(([v, n]) => (
                  <Tag key={v} size="sm">{v} ×{n}</Tag>
                ))}
              </HStack>
            </Box>
          ))}
        </Box>
      )}
      <Heading size="xs" mb={2}>Needs review ({(report.needsReview || []).length})</Heading>
      {(report.needsReview || []).slice(0, 50).map((d) => (
        <Box key={d.documentId} bg={cardBg} borderWidth="1px" borderColor={cardBorder} borderRadius="lg" p={3} mb={2}>
          <HStack justify="space-between" mb={1}>
            <Link to={`${sectionUrl}/documents/${d.documentId}`}>
              <Text fontSize="xs" fontFamily="mono" color="blue.400" noOfLines={1}>{d.pdf}</Text>
            </Link>
            <StatusChip status={d.status} />
          </HStack>
          {d.error && <Text fontSize="xs" color="red.400">{d.error}</Text>}
          {(d.fails || []).length > 0 && <ValidationChecklist checks={d.fails} />}
        </Box>
      ))}
    </>
  );

  const renderCanary = () => (
    <>
      {(report.alarms || []).length > 0 ? (
        <Alert status="error" mb={4} borderRadius="md">
          <AlertIcon />
          <Box>
            <Text fontWeight="bold">{report.alarms.length} CANARY ALARM(S) — model or template drift detected</Text>
            {report.alarms.map((a, i) => (
              <Text key={i} fontSize="sm">{typeof a === 'string' ? a : JSON.stringify(a)}</Text>
            ))}
          </Box>
        </Alert>
      ) : (
        <Alert status="success" mb={4} borderRadius="md">
          <AlertIcon />
          No drift: all {report.docs} canaries reproduce verified values and the template fingerprint holds.
        </Alert>
      )}
      <SimpleGrid columns={{ base: 2, md: 4 }} spacing={3} mb={5}>
        {statCard('Canaries', report.docs ?? '—')}
        {statCard('Goldens', report.goldenDocs ?? '—')}
        {statCard('Silver', report.silverDocs ?? '—')}
        {statCard('Baseline labels', report.baselineLabels ?? '—')}
      </SimpleGrid>
      <Table size="sm">
        <Thead><Tr><Th>PDF</Th><Th>Source</Th><Th>Result</Th></Tr></Thead>
        <Tbody>
          {(report.perDoc || []).map((d, i) => (
            <Tr key={i}>
              <Td fontSize="xs" fontFamily="mono" maxW="420px"><Text noOfLines={1}>{d.pdf}</Text></Td>
              <Td fontSize="xs">{d.source}</Td>
              <Td>
                <Tag size="sm" colorScheme={d.ok ? 'green' : 'red'}>
                  {d.ok ? 'ok' : `${(d.diffs || []).length} diffs`}
                </Tag>
              </Td>
            </Tr>
          ))}
        </Tbody>
      </Table>
    </>
  );

  const renderTune = () => (
    <>
      <Alert status={report.adopted ? 'success' : 'info'} mb={4} borderRadius="md">
        <AlertIcon />
        {report.outcome || (report.adopted ? 'Prompt adopted.' : 'No adoption.')}
      </Alert>
      <SimpleGrid columns={{ base: 2, md: 4 }} spacing={3} mb={5}>
        {statCard('Baseline diffs', report.baseline?.totalDiffs ?? '—')}
        {statCard('Baseline perfect', `${report.baseline?.perfectDocs ?? '—'}/${report.baseline?.docs ?? '—'}`)}
        {statCard('Open corrections', report.corrections?.open ?? '—')}
        {statCard('Resolved by tune', report.resolvedCorrections ?? 0)}
      </SimpleGrid>
      {(report.rounds || []).map((r, i) => (
        <Box key={i} bg={cardBg} borderWidth="1px" borderColor={cardBorder} borderRadius="lg" p={4} mb={3}>
          <HStack justify="space-between" mb={2}>
            <Heading size="xs">Round {r.round ?? i + 1}</Heading>
            <Tag size="sm" colorScheme={r.adopted ? 'green' : 'gray'}>{r.adopted ? 'adopted' : 'rejected'}</Tag>
          </HStack>
          {r.rationale && <Text fontSize="sm" mb={2}>{r.rationale}</Text>}
          {r.result && <Text fontSize="xs" color="gray.500">{r.result}</Text>}
        </Box>
      ))}
      {report.promptBefore && report.adopted && (
        <Text fontSize="xs" color="gray.500">
          The revised prompt is live on the skill (see its version history + tuning log).
        </Text>
      )}
    </>
  );

  const renderClassifyEval = () => (
    <>
      <SimpleGrid columns={{ base: 2, md: 4 }} spacing={3} mb={5}>
        {statCard('Docs', totals.docs ?? '—')}
        {statCard('Type accuracy', fmtPct(totals.typeAccuracy))}
        {statCard('Lender accuracy', fmtPct(totals.lenderAccuracy))}
      </SimpleGrid>
      <Heading size="xs" mb={2}>Misclassified ({(report.misclassified || []).length})</Heading>
      <Table size="sm">
        <Thead><Tr><Th>PDF</Th><Th>Expected</Th><Th>Actual</Th></Tr></Thead>
        <Tbody>
          {(report.misclassified || []).map((m, i) => (
            <Tr key={i}>
              <Td fontSize="xs" fontFamily="mono" maxW="380px"><Text noOfLines={1}>{m.pdf}</Text></Td>
              <Td fontSize="xs">{m.expected?.doc_type}{m.expected?.lender ? ` / ${m.expected.lender}` : ''}</Td>
              <Td fontSize="xs" color="red.400">
                {m.error || `${m.actual?.doc_type}${m.actual?.lender_or_issuer ? ` / ${m.actual.lender_or_issuer}` : ''}`}
              </Td>
            </Tr>
          ))}
        </Tbody>
      </Table>
    </>
  );

  const renderExport = () => (
    <>
      <Alert status={report.exported ? 'success' : 'warning'} mb={4} borderRadius="md">
        <AlertIcon />
        {report.exported
          ? `Exported ${report.examples} example(s) to storage.`
          : `Gates not met — dataset NOT exported. ${report.gate?.detail || ''}`}
      </Alert>
      <SimpleGrid columns={{ base: 2, md: 4 }} spacing={3} mb={5}>
        {statCard('Examples', report.examples ?? 0)}
        {statCard('Goldens', report.goldenCount ?? 0)}
        {statCard('Silver', report.silverCount ?? 0)}
      </SimpleGrid>
      {report.storage && (
        <Box bg={cardBg} borderWidth="1px" borderColor={cardBorder} borderRadius="lg" p={4}>
          <Text fontSize="sm" mb={1}>JSONL: <Code fontSize="xs">{report.storage.jsonlPath}</Code></Text>
          <Text fontSize="sm">Manifest: <Code fontSize="xs">{report.storage.manifestPath}</Code></Text>
          <Text fontSize="xs" color="gray.500" mt={2}>
            Files live in the docai-documents bucket under exports/. Pull them with the CLI or Supabase dashboard.
          </Text>
        </Box>
      )}
    </>
  );

  const body = () => {
    if (run.status === 'error') {
      return (
        <Alert status="error" borderRadius="md">
          <AlertIcon />
          {run.error || 'Run failed.'}
        </Alert>
      );
    }
    if (run.status !== 'done') {
      return <Text fontSize="sm" color="gray.500">Report appears when the run completes.</Text>;
    }
    switch (run.kind) {
      case 'eval': return renderEval();
      case 'fleet': return renderFleet();
      case 'canary': return renderCanary();
      case 'tune': return renderTune();
      case 'classify-eval': return renderClassifyEval();
      case 'export-finetune': return renderExport();
      default:
        return (
          <Code as="pre" display="block" w="100%" p={3} fontSize="xs" whiteSpace="pre-wrap" borderRadius="md" bg={codeBg} maxH="70vh" overflowY="auto">
            {JSON.stringify(report, null, 2)}
          </Code>
        );
    }
  };

  return (
    <Box p={6}>
      <HStack mb={4} spacing={3} flexWrap="wrap">
        <Button as={Link} to={`${sectionUrl}/runs`} size="sm" variant="ghost" leftIcon={<FiArrowLeft />} />
        <Heading size="md">{run.kind} run</Heading>
        <StatusChip status={run.status} />
        {run.skill_name && <Tag size="sm">{run.skill_name}</Tag>}
        <Text fontSize="xs" color="gray.500">{new Date(run.created_at).toLocaleString()}</Text>
        <Button size="sm" variant="ghost" leftIcon={<FiRefreshCw />} onClick={refresh} isLoading={isPolling && run.status === 'running'} />
      </HStack>

      {['queued', 'running'].includes(run.status) && (
        <Box mb={5}><RunProgressBar run={run} /></Box>
      )}

      {body()}
    </Box>
  );
}
